import * as THREE from 'three';
import {
  OBJECT_CONFIGS,
  resolveObjectConfig,
  canonicalObjectType,
} from './objectTypeConfigs';
import {
  findFloorYPosition,
  findNearestWallInDirection,
} from './findNearestWall';
import {
  findNearestWall,
  findWallBehindObject,
} from '../../../utils/snapToWall';
import {
  getMountConfig,
  getVerticalPosition,
} from '../../../constants/objectMounting';

/**
 * Get configuration for an object type
 *
 * The optional `doc` argument lets callers branch on per-instance fields.
 * For toilets with `doc.mountType === 'wall-hung'`, the returned config
 * flips to a wall-mount variant (not floor-mounted, anchored at the ADA
 * rim height) so drag-time snapping behaves consistently with the
 * placement-time decision made by `placeToilet`.
 *
 * @param {string} objectType - The type of object
 * @param {string|Object} [category] - Object category (string or {key: true} object)
 * @param {Object} [doc] - Optional fixture doc/instance (looks for `mountType`)
 * @returns {Object} Object configuration
 */
export const getObjectConfig = (objectType, category, doc = null) => {
  const base = resolveObjectConfig(objectType, category);
  if (objectType === 'toilet' && doc?.mountType === 'wall-hung') {
    return {
      ...base,
      floorMounted: false,
      mountHeight: 0.43, // ADA rim height; matches MOUNT_HEIGHTS.TOILET_WALL_HUNG_RIM
      wallOffset: 0,
      verticalAlignment: 'center',
    };
  }
  return base;
};

/**
 * `snapObjectToWall` lived here and was DEAD — exported, never imported. It was
 * also the only reader of `OBJECT_CONFIGS.floorMounted`, which made that field
 * look load-bearing when nothing consumed it. Removed rather than left as a
 * third implementation of "how does this attach to the world?" beside
 * `calculateObjectPosition` (the live one) and the two config tables.
 *
 * If you need wall snapping, `calculateObjectPosition` is the live path.
 */

/**
 * Check if an object should snap to walls based on its type
 */
export const shouldSnapToWall = (objectType, category, doc = null) => {
  const config = getObjectConfig(objectType, category, doc);
  return config.requiresWall || config.requiresCorner;
};

/**
 * Get the appropriate wall offset for an object type
 */
export const getWallOffset = (objectType, category, doc = null) => {
  const config = getObjectConfig(objectType, category, doc);
  return config.wallOffset;
};

/**
 * Get default properties for an object type
 */
export const getObjectDefaults = (objectType) => {
  // Direct type match
  // Canonical name first: `stove` and `range` are one appliance, and this
  // lookup does not go through `resolveObjectConfig`.
  let config = OBJECT_CONFIGS[canonicalObjectType(objectType)];

  // If no direct match, try category mappings
  if (!config) {
    // Map common category names to their config keys
    const categoryMappings = {
      'overhead-wall-light': 'wall-light',
      'ceiling-light': 'ceiling-light',
    };

    const mappedType = categoryMappings[objectType];
    if (mappedType) {
      config = OBJECT_CONFIGS[mappedType];
    }
  }

  // Fall back to default if still no match
  if (!config) {
    config = OBJECT_CONFIGS.default;
  }

  return {
    wallAlignment: config.wallAlignment,
    verticalAlignment: config.verticalAlignment,
    wallOffset: config.wallOffset,
    mountHeight: config.mountHeight,
  };
};

/**
 * Initialize object with default properties when created
 */
export const initializeObject = (object) => {
  const defaults = getObjectDefaults(object.type);

  // Only set if not already defined (preserves user overrides)
  return {
    ...object,
    wallAlignment: object.wallAlignment ?? defaults.wallAlignment,
    verticalAlignment: object.verticalAlignment ?? defaults.verticalAlignment,
  };
};

// Note: Wall-finding logic is now provided by the unified snapToWall utility
// Helper functions removed - use findNearestWall from snapToWall.js instead

/**
 * Apply vertical alignment based on object's verticalAlignment property
 */
const applyVerticalAlignment = ({
  currentPosition,
  verticalAlignment,
  oldDimensions,
  newDimensions,
  floorY,
}) => {
  const oldHeight = oldDimensions?.y || 1;
  const newHeight = newDimensions?.y || 1;
  const heightDiff = newHeight - oldHeight;

  switch (verticalAlignment) {
    case 'bottom':
      // Maintain bottom position (base stays at same height)
      // Object grows/shrinks upward
      return {
        ...currentPosition,
        y: currentPosition.y + heightDiff / 2,
      };

    case 'top':
      // Maintain top position
      // Object grows/shrinks downward
      return {
        ...currentPosition,
        y: currentPosition.y - heightDiff / 2,
      };

    case 'center':
    default:
      // Maintain center position
      // Object grows/shrinks equally in both directions
      return currentPosition;
  }
};

/**
 * Apply wall alignment with objects always facing outward from wall
 */
const alignToNearestWall = ({
  position,
  dimensions,
  objectType,
  wallAlignment,
  walls,
  currentRotation = null, // Object's current rotation (if already positioned)
  maxDistance = 1.5, // 1.5 meters (about 5 feet) max snapping distance
  surroundPolygon = null, // tub-surround-polygon for corner bathtubs
  usePolygonSurround = false, // explicit flag for polygon surround snapping
}) => {
  if (wallAlignment === 'none') {
    return { aligned: false, position, rotation: { x: 0, y: 0, z: 0 } };
  }

  const config =
    OBJECT_CONFIGS[canonicalObjectType(objectType)] || OBJECT_CONFIGS.default;

  // For wall-mounted objects that already have a meaningful rotation, find the wall BEHIND them
  // For center-aligned objects (doors/windows), ALWAYS use omnidirectional search
  // For NEW placements (rotation is default 0), also use omnidirectional search
  // because they should snap to the nearest wall regardless of current rotation
  let wallInfo;
  const hasNonZeroRotation =
    currentRotation &&
    currentRotation.y !== undefined &&
    currentRotation.y !== 0;
  const shouldUseDirectionalSearch =
    wallAlignment === 'surface' && hasNonZeroRotation && config.requiresWall;

  if (shouldUseDirectionalSearch) {
    wallInfo = findWallBehindObject(
      position,
      currentRotation.y,
      walls,
      maxDistance
    );
  } else {
    // Use unified wall-finding utility from snapToWall.js
    // This handles new placements and center-aligned objects
    wallInfo = findNearestWall(position, walls, maxDistance);
  }

  // Convert to format expected by rest of function
  const nearestWall = wallInfo
    ? {
        wall: wallInfo.wall,
        distance: wallInfo.distance,
        closestPoint: wallInfo.point,
        normal: wallInfo.normal,
      }
    : null;

  if (!nearestWall || nearestWall.distance > maxDistance) {
    return { aligned: false, position, rotation: { x: 0, y: 0, z: 0 } };
  }

  let snappedPosition = { ...position };
  let rotation = { x: 0, y: 0, z: 0 };

  if (wallAlignment === 'surface') {
    // Determine which side of the wall the cursor/position is on
    const toPosition = {
      x: position.x - nearestWall.closestPoint.x,
      z: position.z - nearestWall.closestPoint.z,
    };

    // Dot product to determine side
    const dotProduct =
      toPosition.x * nearestWall.normal.x + toPosition.z * nearestWall.normal.z;

    // Use the normal pointing toward the cursor side
    const sideNormal = {
      x: dotProduct >= 0 ? nearestWall.normal.x : -nearestWall.normal.x,
      z: dotProduct >= 0 ? nearestWall.normal.z : -nearestWall.normal.z,
    };

    // Position object at wall surface with appropriate offset
    let halfDepthAlongNormal = dimensions.z / 2;
    if (
      usePolygonSurround &&
      surroundPolygon?.points?.length >= 3 &&
      (objectType === 'bathtub' || objectType === 'tub-support-box')
    ) {
      const rotY = currentRotation?.y || 0;
      const I2M = 0.0254;
      let maxProj = 0;
      for (const p of surroundPolygon.points) {
        const mx = p.x * I2M;
        const mz = p.z * I2M;
        const rx = mx * Math.cos(rotY) - mz * Math.sin(rotY);
        const rz = mx * Math.sin(rotY) + mz * Math.cos(rotY);
        const proj = Math.abs(rx * sideNormal.x + rz * sideNormal.z);
        if (proj > maxProj) maxProj = proj;
      }
      halfDepthAlongNormal = maxProj;
    }
    const offsetDistance = config.wallOffset + halfDepthAlongNormal;

    // Wall offset calculated

    snappedPosition = {
      x: nearestWall.closestPoint.x + sideNormal.x * offsetDistance,
      y: position.y, // Maintain vertical position
      z: nearestWall.closestPoint.z + sideNormal.z * offsetDistance,
    };

    // Wall snap position calculated

    // Calculate rotation so object faces correctly relative to wall
    if (config.facesWall) {
      // Object faces the wall (like mirror, sink)
      rotation.y = Math.atan2(-sideNormal.x, -sideNormal.z);
    } else {
      // Object faces away from wall (like toilet, wall lights)
      rotation.y = Math.atan2(sideNormal.x, sideNormal.z);
    }
  } else if (wallAlignment === 'center') {
    // Position object at wall center (for doors, windows, openings)
    snappedPosition = {
      x: nearestWall.closestPoint.x,
      y: position.y,
      z: nearestWall.closestPoint.z,
    };

    // Align rotation with wall direction
    // Calculate rotation from wall endpoints for reliability
    const wallStart = nearestWall.wall.start || {
      x:
        nearestWall.wall.center.x -
        (Math.cos(nearestWall.wall.rotation?.y || 0) *
          (nearestWall.wall.dimensions?.x || 0)) /
          2,
      z:
        nearestWall.wall.center.z -
        (Math.sin(nearestWall.wall.rotation?.y || 0) *
          (nearestWall.wall.dimensions?.x || 0)) /
          2,
    };
    const wallEnd = nearestWall.wall.end || {
      x:
        nearestWall.wall.center.x +
        (Math.cos(nearestWall.wall.rotation?.y || 0) *
          (nearestWall.wall.dimensions?.x || 0)) /
          2,
      z:
        nearestWall.wall.center.z +
        (Math.sin(nearestWall.wall.rotation?.y || 0) *
          (nearestWall.wall.dimensions?.x || 0)) /
          2,
    };

    // Calculate wall direction vector
    const wallDir = {
      x: wallEnd.x - wallStart.x,
      z: wallEnd.z - wallStart.z,
    };

    // Calculate base rotation from wall direction
    // atan2(z, x) gives angle in XZ plane
    let baseRotation = Math.atan2(wallDir.z, wallDir.x);

    // For center-aligned objects (doors/windows), use the parent wall's rotation
    // so the door/window faces the same direction as the wall
    rotation.y = nearestWall.wall.rotation?.y || 0;
  }

  return {
    aligned: true,
    position: snappedPosition,
    rotation,
    distance: nearestWall.distance,
    wall: nearestWall.wall,
    wallNormal: nearestWall.normal,
  };
};

/**
 * Unified object positioning for all views
 * Handles both wall snapping and vertical alignment
 * Works for all objects regardless of rendering method (GLB, geometric, etc.)
 * @param {Object} params
 * @param {Object} params.object - Object with position, dimensions, wallAlignment, verticalAlignment
 * @param {Object} params.newDimensions - New dimensions (from any source) {x, y, z}
 * @param {Array} params.walls - Array of wall objects from structure data
 * @param {number} params.floorY - Y position of floor
 * @param {Object} params.previousDimensions - Previous dimensions for alignment calculations
 * @param {boolean} params.preserveVerticalAnchor - The object is ALREADY PLACED
 *   and only its size changed, so hold its existing vertical anchor (bottom by
 *   default) and let the new height grow from there. False is first placement:
 *   seat the object at its type's mount height. Getting this wrong is silent —
 *   a wall-mounted object re-seats to its default height and the user's own
 *   mount height is gone (was: `isModelChange`, which described ONE of the two
 *   callers that need it rather than the behaviour, so a plain size edit did
 *   not think to pass it).
 * @returns {Object} New position and rotation
 */
export const calculateObjectPosition = ({
  object,
  newDimensions,
  walls,
  floorY = 0,
  previousDimensions,
  preserveVerticalAnchor = false,
  ceilingY = null,
}) => {
  let position = { ...object.center };
  let rotation = { ...object.rotation };
  let snappedWall = null;
  let snappedWallNormal = null;

  // Get mount configuration from constants
  const mountConfig = getMountConfig(object.type);
  const objectHeight = newDimensions?.y || 1;

  // STEP 1: HORIZONTAL SNAPPING (XZ plane + rotation)
  // Snap to wall if object requires wall attachment
  if (
    mountConfig.requiresWall &&
    object.wallAlignment &&
    object.wallAlignment !== 'none'
  ) {
    const snapResult = alignToNearestWall({
      position: position,
      dimensions: newDimensions,
      objectType: object.type,
      wallAlignment: object.wallAlignment,
      walls: walls,
      currentRotation: object.rotation, // Use rotation to find wall behind object
      surroundPolygon: object['tub-surround-polygon'],
      usePolygonSurround: object.usePolygonSurround === true,
    });

    if (snapResult.aligned) {
      // Update XZ position and rotation
      position.x = snapResult.position.x;
      position.z = snapResult.position.z;
      rotation = snapResult.rotation;
      snappedWall = snapResult.wall || null;
      snappedWallNormal = snapResult.wallNormal || null;
    }
  }

  // STEP 2: VERTICAL SNAPPING (Y position)
  // Always snap vertically based on mount type
  if (preserveVerticalAnchor) {
    // Already placed: hold the anchor and absorb the size change into it.
    position = applyVerticalAlignment({
      currentPosition: position,
      verticalAlignment: object.verticalAlignment || 'bottom',
      oldDimensions: previousDimensions,
      newDimensions: newDimensions,
      floorY: floorY,
    });
  } else {
    // Initial placement: snap to mount type
    // `null` means the type is COUNTER-mounted: held by a slab whose height
    // this module cannot see, already seated by kitchen post-processing.
    // Leave Y exactly as it is.
    const mountedY = getVerticalPosition(
      object.type,
      floorY,
      objectHeight,
      ceilingY
    );
    if (mountedY !== null) position.y = mountedY;
  }

  return { position, rotation, snappedWall, snappedWallNormal };
};
